import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/lib/queryClient';
import { apiService } from '@/services/api';
import { clearAuthStorage } from '@/utils/errorHandling';
import type { MemberRole } from '@/types/client';

interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  avatar_url?: string;
  member_role?: MemberRole;
  client_type?: 'independent' | 'central' | 'member';
  status?: 'prospect' | 'actif' | 'inactif' | 'bloque';
  is_email_verified: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refresh_token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: (navigate?: (path: string) => void) => void;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>()(persist(
  (set) => ({
    user: null,
    token: null,
    refresh_token: null,
    isAuthenticated: false,
    isLoading: false,

    login: async (email: string, password: string) => {
      try {
        set({ isLoading: true });
        let data: any;
        let userType: 'admin' | 'super_admin' | 'apporteur' | 'buyer' | 'buyer_member' | 'employee' = 'admin';
        
        // Utiliser l'endpoint de connexion unifié
        data = await apiService.login(email, password);
        
        // Déterminer le type d'utilisateur à partir du token JWT
        const token = data?.access_token;
        if (token) {
          try {
            // Décoder le payload JWT pour obtenir le scope
            const base64Payload = token.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));
            const scope = payload.scope;
            
            console.log('[AUTH] JWT scope:', scope);
            
            if (scope === 'buyer_member') {
              userType = 'buyer_member';
              console.log('[AUTH] Connexion réussie en tant que membre du client');
            } else if (scope === 'buyer') {
              userType = 'buyer';
              console.log('[AUTH] Connexion réussie en tant que client');
            } else if (scope === 'employee') {
              userType = 'employee';
              console.log('[AUTH] Connexion réussie en tant qu\'employé');
            } else if (scope === 'apporteur') {
              userType = 'apporteur';
              console.log('[AUTH] Connexion réussie en tant qu\'apporteur');
            } else if (scope === 'super_admin') {
              userType = 'super_admin';
              console.log('[AUTH] Connexion réussie en tant que super administrateur');
            } else {
              userType = 'admin';
              console.log('[AUTH] Connexion réussie en tant qu\'administrateur');
            }
          } catch (decodeErr) {
            console.warn('[AUTH] Could not decode JWT token, defaulting to admin');
            userType = 'admin';
          }
        } else {
          userType = 'admin';
        }

        const access_token: string = data?.access_token;
        const refresh_token_value: string | null = data?.refresh_token ?? null;

        // Stocker le token et le type d'utilisateur
        set({
          token: access_token,
          refresh_token: refresh_token_value,
          isAuthenticated: true,
        });
        
        // Sauvegarder le type d'utilisateur
        localStorage.setItem('userType', userType);

        // Fetch current user selon le type
        try {
          let me: any;
          
          console.log('[AUTH] Processing user data:', { userType, data });
          
          if (userType === 'buyer_member') {
            // Pour les membres, utiliser getCurrentUser mais forcer le rôle
            console.log('[AUTH] Fetching buyer member via getCurrentUser API');
            me = await apiService.getCurrentUser();
            // Forcer le rôle à buyer_member pour assurer la cohérence
            me.role = 'buyer_member';
            // member_role and buyer_id should already be set by the /auth/me endpoint
          } else {
            // Pour admin/buyer/employee, utiliser l'API standard
            console.log('[AUTH] Fetching user via getCurrentUser API');
            me = await apiService.getCurrentUser();
          }
          
          console.log('[AUTH] Final user object:', me);

          let normalizedRole = me?.role;
          if (normalizedRole === 'buyer') {
            normalizedRole = 'client';
          } else if (normalizedRole === 'buyer_member') {
            normalizedRole = 'client_member';
          }
          const normalizedUser = { ...me, role: normalizedRole };

          set({ user: normalizedUser, isLoading: false });
          queryClient.clear();
        } catch (fetchErr) {
          console.error('Failed to fetch current user after login:', fetchErr);
          set({ user: null, token: null, refresh_token: null, isAuthenticated: false, isLoading: false });
          throw fetchErr;
        }
      } catch (error) {
        console.error('Login error:', error);
        set({ isLoading: false });
        throw error;
      }
    },

    logout: (navigate?: (path: string) => void) => {
      set({
        user: null,
        token: null,
        refresh_token: null,
        isAuthenticated: false,
      });
      
      // Explicitly clear localStorage to ensure auth data is removed
      clearAuthStorage();
      
      // Clear query cache on logout
      queryClient.clear();
      
      // Navigate to login page - use window.location for more reliable redirect
      if (navigate) {
        try {
          navigate('/login');
        } catch (error) {
          console.warn('Navigate function failed, using window.location:', error);
          window.location.href = '/login';
        }
      } else {
        // Fallback to window.location if no navigate function
        window.location.href = '/login';
      }
    },

    setUser: (user: User) => {
      let normalizedRole = user.role;
      if (normalizedRole === 'buyer') {
        normalizedRole = 'client';
      } else if (normalizedRole === 'buyer_member') {
        normalizedRole = 'client_member';
      }
      set({ user: { ...user, role: normalizedRole } });
    },
  }),
  {
    name: 'auth-storage',
    partialize: (state) => ({
      user: state.user,
      token: state.token,
      refresh_token: state.refresh_token,
      isAuthenticated: state.isAuthenticated,
    }),
  }
));
